import {
  forwardRef,
  useId,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../../utils/classes";
import fieldStyles from "../text-field/text-field.module.css";
import styles from "./textarea-field.module.css";

export type TextareaFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  description?: string;
  error?: string;
  wrapperClassName?: string;
};

export const TextareaField = forwardRef<
  HTMLTextAreaElement,
  TextareaFieldProps
>(function TextareaField(
  {
    label,
    description,
    error,
    wrapperClassName,
    className,
    id,
    required,
    "aria-describedby": describedBy,
    rows = 4,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;
  return (
    <div className={cn(fieldStyles.field, wrapperClassName)}>
      {label != null && (
        <label className={fieldStyles.label} htmlFor={inputId}>
          {label}
          {required && (
            <span className={fieldStyles.required} aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
      )}
      <textarea
        {...props}
        ref={ref}
        id={inputId}
        required={required}
        rows={rows}
        aria-invalid={error ? true : props["aria-invalid"]}
        aria-describedby={
          [describedBy, description && descriptionId, error && errorId]
            .filter(Boolean)
            .join(" ") || undefined
        }
        className={cn(styles.input, className)}
      />
      {description && (
        <p id={descriptionId} className={fieldStyles.description}>
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className={fieldStyles.error}>
          {error}
        </p>
      )}
    </div>
  );
});
